import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Formik, Field } from 'formik'
import { Link, useLocation } from 'react-router-dom'
import * as Yup from 'yup'
import { isEmpty } from 'lodash'
import { IconCircleDashedCheck, IconExclamationCircle } from '@tabler/icons-react'
import pluralize from 'pluralize'
import queryString from 'query-string'
import classNames from 'classnames'

import Select from '@/components/forms/Select'
import { PageHeader } from '@/components/headers/PageHeader'
import { Button } from '@/components/buttons/Button'
import { fetchPlaybooksByOrg } from '@/reducers/analytics/analytics.actions'
import { getOrganizationOptions } from '@/utils/helpers'
import { fetchAgentsByOrg } from '@/reducers/callSearch/callSearch.actions'
import { RadioGroup } from '@/components/forms/formik'
import { DragAndDropFileUploader } from '@/components/forms/formik/file-uploader/DragAndDropFileUploader'
import { FormField } from '@/components/forms/FormField'
import { uploadAudioFiles } from '@/reducers/audioIngestion/audioIngestion.actions'
import { clearFiles } from '@/reducers/audioIngestion/audioIngestion.redux'

import './UploadCallsPage.scss'

const ACCEPTED_AUDIO_TYPES = {
  'audio/wav': ['.wav', '.wave'],
  'audio/mp3': ['.mp3'],
  'audio/mp4': ['.mp4', '.m4a'],
  'audio/flac': ['.flac'],
  'audio/aac': ['.aac'],
  'audio/ogg': ['.ogg', '.opus'],
  'audio/webm': ['.webm'],
  'video/x-flv': ['.flv'],
}

const ACCEPTED_FILES_MESSAGE = (
  <>
    <span>
      Supported formats: .wav, .wave, .mp3, .mp4, .m4a, .flac, .aac, .ogg, .opus, .webm, .flv
    </span>
    <br />
    <span>Max file size: 250MB</span>
  </>
)

const UploadCallsPage = () => {
  const dispatch = useDispatch()
  const { search } = useLocation()
  const { organization_id: urlOrgId, playbook: urlPlaybookCid } = queryString.parse(search)
  const { organizationid, username, token } = useSelector((state) => state.currentUser)
  const analyticsStore = useSelector((state) => state.analytics)
  const { data, loading: analyticsLoading } = analyticsStore
  const { organizations } = useSelector((state) => state)
  const { loading, data: agentData } = useSelector((state) => state.callSearch)
  const { files } = useSelector((state) => state.audioIngestion)
  const organizationOptions = getOrganizationOptions(organizations)
  const [isUploadComplete, setIsUploadComplete] = useState(false)

  const isAdmin = organizationid === 1
  const maxFileSize = 262144000 // 250mb
  const maxFiles = 100

  const validationSchema = Yup.object().shape({
    organization: Yup.string().required(),
    agent: Yup.string().required(),
    config_cid: Yup.string().required(),
    files: Yup.array().min(1).required(),
  })

  const initialValues = {
    organization: isAdmin ? (urlOrgId ? Number(urlOrgId) : null) : organizationid,
    agent: null,
    config_cid: urlPlaybookCid || null,
    agent_channel: '-1',
    files: [],
  }

  const handleOrgSelect = (selectedOrg) => {
    dispatch(fetchPlaybooksByOrg(selectedOrg))
    dispatch(fetchAgentsByOrg(selectedOrg))
  }

  useEffect(() => {
    if (!isAdmin) {
      dispatch(fetchPlaybooksByOrg(organizationid))
      dispatch(fetchAgentsByOrg(organizationid))
    } else if (urlOrgId) {
      handleOrgSelect(Number(urlOrgId))
    }
  }, [])

  useEffect(() => {
    if (!isEmpty(files)) {
      const totalFilesComplete = files.filter((file) => file.isDone).length
      setIsUploadComplete(totalFilesComplete === files.length)
    }
  }, [files])

  const getTotalFilesDone = () => {
    if (isEmpty(files)) {
      return 0
    }
    return files.filter((file) => file.isDone).length
  }

  const getSuccessfulFiles = () => {
    if (isEmpty(files)) {
      return 0
    }
    return files.filter((file) => file.isDone && !file.isError).length
  }

  const getErrorFiles = () => {
    if (isEmpty(files)) {
      return 0
    }
    return files.filter((file) => file.isError).length
  }

  const getCallExplorerLink = (orgId) => {
    const callIds = files
      .map((file) => {
        if (file.callId) {
          return `call_ids=${file.callId}`
        }
        return ''
      })
      .join('&')

    return `/explorer?${callIds}&organizationId=${orgId}`
  }

  return (
    <>
      <PageHeader title="Upload Calls" data-testid="upload-calls-page" />
      <Formik
        initialValues={initialValues}
        onSubmit={(values, { setSubmitting }) => {
          dispatch(uploadAudioFiles(values, username, token, organizationid, setSubmitting))
        }}
        validationSchema={validationSchema}
        validateOnMount
      >
        {({ errors, submitForm, resetForm, isSubmitting, ...props }) => {
          const hasFiles = !isEmpty(props.values.files)
          const totalFiles = props.values.files.length
          const canStartUpload = isEmpty(errors)
          const successCount = getSuccessfulFiles()
          const errorCount = getErrorFiles()
          const doneCount = getTotalFilesDone()

          let filesCardTitle = null
          if (isUploadComplete) {
            filesCardTitle = `Files Uploaded (${totalFiles})`
          } else if (isSubmitting) {
            filesCardTitle = `Files uploading... (${totalFiles})`
          } else if (hasFiles) {
            filesCardTitle = `Files ready for upload (${totalFiles})`
          }

          const showFooter = hasFiles || isSubmitting || isUploadComplete
          const isDropzoneEmpty = !hasFiles && !isSubmitting && !isUploadComplete

          return (
            <div className="upload-calls">
              <div className="upload-calls__card upload-calls__card--form">
                <h2 className="upload-calls__card-title">Upload settings</h2>
                <hr className="upload-calls__divider" />
                <div className="upload-calls__form-body">
                  {isAdmin && (
                    <FormField>
                      <label>Organization</label>
                      <div data-testid="org-dropdown">
                        <Select
                          options={organizationOptions}
                          label="Organization"
                          value={props.values.organization}
                          onChange={(event) => {
                            props.setFieldValue('organization', event.value)
                            handleOrgSelect(event.value)
                          }}
                          placeholder="Select an Organization"
                          isClearable={false}
                          isDisabled={isSubmitting}
                        />
                      </div>
                    </FormField>
                  )}
                  <div className="upload-calls__form-row">
                    <FormField>
                      <label>Playbook</label>
                      <div data-testid="playbook-dropdown">
                        <Select
                          placeholder="Select a Playbook"
                          loading={analyticsLoading.playbooks}
                          options={data.playbooks}
                          label="Playbook"
                          value={props.values.config_cid}
                          onChange={(event) => props.setFieldValue('config_cid', event.value)}
                          className="playbook-select"
                          isClearable={false}
                          fixedWidth
                          isDisabled={isSubmitting}
                        />
                      </div>
                    </FormField>
                    <FormField>
                      <label>Agents</label>
                      <div data-testid="agent-dropdown">
                        <Select
                          placeholder="Assign calls to an agent"
                          label="Agent"
                          options={agentData.agents}
                          loading={loading.agents}
                          value={props.values.agent}
                          onChange={(agent) => props.setFieldValue('agent', agent.value)}
                          fixedWidth
                          isDisabled={isSubmitting}
                        />
                      </div>
                    </FormField>
                  </div>
                  <div className="upload-calls__radio-group">
                    <div className="upload-calls__radio-legend">
                      Which side of the recording has the agent&apos;s voice?
                    </div>
                    <p className="upload-calls__radio-helper">
                      We use this to tell the agent and customer apart in your analytics.
                    </p>
                    <Field
                      name="agent_channel"
                      component={RadioGroup}
                      options={[
                        { label: 'Autodetect', value: '-1' },
                        { label: 'Left', value: '0' },
                        { label: 'Right', value: '1' },
                      ]}
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>

              <div
                className={classNames('upload-calls__card', 'upload-calls__card--files', {
                  'upload-calls__card--dropzone': isDropzoneEmpty,
                })}
              >
                {isDropzoneEmpty && (
                  <svg
                    className="upload-calls__dropzone-border"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <rect />
                  </svg>
                )}
                {filesCardTitle && <h2 className="upload-calls__card-title">{filesCardTitle}</h2>}
                <div className="upload-calls__upload-card-body">
                  <DragAndDropFileUploader
                    acceptedFileTypes={ACCEPTED_AUDIO_TYPES}
                    acceptedFileMessage={ACCEPTED_FILES_MESSAGE}
                    imageCache=""
                    name="files"
                    maxSize={maxFileSize}
                    maxFiles={maxFiles}
                    {...props}
                    multiple
                    cardVariant
                    useIconLayout={false}
                    allowEdits={!isSubmitting}
                  />
                </div>

                {showFooter && (
                  <>
                    <hr className="upload-calls__divider" />
                    <div className="upload-calls__footer" data-testid="submit-button-container">
                      <div className="upload-calls__footer-status">
                        {!isSubmitting && !isUploadComplete && hasFiles && (
                          <div>
                            {pluralize('file', totalFiles, true)} ready. Check the settings above
                            before you upload.
                          </div>
                        )}
                        {isSubmitting && !isUploadComplete && (
                          <>
                            <div className={classNames('status-success', 'flex-align-center')}>
                              <IconCircleDashedCheck className="icon-svg" />
                              <div>{`${doneCount} of ${totalFiles} uploaded`}</div>
                            </div>
                            <div>Uploading. Keep this tab open until it finishes.</div>
                          </>
                        )}
                        {isUploadComplete && (
                          <div>
                            {successCount > 0 && errorCount === 0 && (
                              <div className={classNames('status-success', 'flex-align-center')}>
                                {`${successCount} of ${totalFiles} files uploaded.`}
                              </div>
                            )}
                            {successCount > 0 && errorCount > 0 && (
                              <div>{`${successCount} uploaded. ${errorCount} failed.`}</div>
                            )}
                            {successCount === 0 && errorCount > 0 && (
                              <div className={classNames('status-critical', 'flex-align-center')}>
                                <IconExclamationCircle className="icon-svg" />
                                <div>{`${errorCount} of ${totalFiles} files failed to upload.`}</div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {!isSubmitting && !isUploadComplete && (
                        <Button brand type="submit" onClick={submitForm} disabled={!canStartUpload}>
                          Start Upload
                        </Button>
                      )}
                      {isSubmitting && !isUploadComplete && (
                        <Button loading brand>
                          Uploading...
                        </Button>
                      )}
                      {isUploadComplete && (
                        <div className="upload-calls__footer-actions">
                          <Button
                            onClick={() => {
                              resetForm()
                              dispatch(clearFiles())
                              setIsUploadComplete(false)
                            }}
                          >
                            Start over
                          </Button>
                          {successCount > 0 && (
                            <Link
                              className="balto-button brand"
                              to={getCallExplorerLink(props.values.organization)}
                              target="_blank"
                              data-testid="call-explorer-link"
                            >
                              View in Explorer
                            </Link>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )
        }}
      </Formik>
    </>
  )
}

export default UploadCallsPage
